import type { ApiSuccess, Paginated } from './api';

/**
 * A "Join the crew" application from the public careers form: who they are,
 * the field they work in, and their CV. The admin works it through these
 * statuses; `rejected` and `hired` are final.
 */
export const JOB_APPLICATION_STATUSES = [
  'submitted',
  'in_review',
  'shortlisted',
  'hired',
  'rejected',
] as const;
export type JobApplicationStatus = (typeof JOB_APPLICATION_STATUSES)[number];

/**
 * The roles an applicant can apply for: the careers form's "Role" dropdown,
 * the API's validation, and the admin's filter all read this list. The
 * database keeps its own copy in `job_applications_work_field_chk`, so a
 * change here needs a migration too.
 */
export const WORK_FIELDS = [
  'Café & Barista',
  'Front Desk',
  'Community & Events',
  'IT & Tech Support',
  'Training',
  'Marketing',
  'Other',
] as const;
export type WorkField = (typeof WORK_FIELDS)[number];

/** What `POST /api/applications` answers with. */
export interface JobApplicationReceipt {
  received: true;
  /** Human reference, e.g. `APP-0042`. */
  reference: string;
}
export type JobApplicationReceiptResponse = ApiSuccess<JobApplicationReceipt>;

export interface JobApplicationCv {
  fileName: string;
  /** `application/pdf` or a Word type — the form takes nothing else. */
  mimeType: string;
  sizeBytes: number;
  /** A short-lived signed link. Null until the API issues one. */
  url: string | null;
}

export interface JobApplicationEvent {
  id: string;
  fromStatus: JobApplicationStatus | null;
  toStatus: JobApplicationStatus;
  note: string | null;
  /** The administrator who moved it; null for the guest's own submission. */
  actorName: string | null;
  occurredAt: string;
}

export interface AdminJobApplication {
  id: string;
  /** Human reference, e.g. `APP-0412`. */
  reference: string;
  fullName: string;
  email: string;
  /** E.164, e.g. `+96171123456`. */
  phone: string;
  workField: WorkField;
  cv: JobApplicationCv;
  status: JobApplicationStatus;
  /** Staff-only notes. Never shown to the applicant. */
  notes: { id: string; body: string; authorName: string; createdAt: string }[];
  history: JobApplicationEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface JobApplicationListQuery {
  page?: number;
  limit?: number;
  status?: JobApplicationStatus;
  workField?: WorkField;
  search?: string;
  sort?: 'createdAt' | 'fullName' | 'reference';
  direction?: 'asc' | 'desc';
}

export type JobApplicationList = Paginated<AdminJobApplication>;

export type AdminJobApplicationResponse = ApiSuccess<AdminJobApplication>;

/** Count per status, for the summary strip above the list. */
export type JobApplicationCounts = Record<JobApplicationStatus, number>;
export type JobApplicationCountsResponse = ApiSuccess<JobApplicationCounts>;
